import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';

import { AnnexLineFormTypeEnum } from '@enums/annex-line-form-type.enum';
import { ContractAnnexLineCharge } from '@gi/enums/contract-annex-line-charge.enum';
import { ContractAnnexLine } from '@gi/models/contract-annex-line';
import { FormHelper } from '@helpers/form.helper';
import { ErrorMessage } from '@interfaces/error-message';

function discountPercentageRequiredValidator(): ValidatorFn {
  return (form: ContractAnnexLineForm) => {
    form.discountPercentage.removeValidators(Validators.required);

    if (form.chargeChoice.value === ContractAnnexLineCharge.discount) {
      form.discountPercentage.addValidators(Validators.required);
    }

    return null;
  };
}

export type ContractAnnexLineFormType = {
  id: FormControl<number | undefined>;
  type: FormControl<AnnexLineFormTypeEnum | null>;
  label: FormControl<string | null>;
  chargeChoice: FormControl<string | null>;
  discountPercentage: FormControl<number | null>;
};

export class ContractAnnexLineForm extends FormGroup<ContractAnnexLineFormType> {
  get id(): FormControl<number | undefined> {
    return this.controls.id;
  }

  get type(): FormControl<AnnexLineFormTypeEnum | null> {
    return this.controls.type;
  }

  get label(): FormControl<string | null> {
    return this.controls.label;
  }

  get chargeChoice(): FormControl<string | null> {
    return this.controls.chargeChoice;
  }

  get chargeChoiceErrorMessage(): ErrorMessage | null {
    return FormHelper.getEmptyErrorMessage(this.chargeChoice, 'Le type de charge est requis');
  }

  get discountPercentage(): FormControl<number | null> {
    return this.controls.discountPercentage;
  }

  get discountPercentageErrorMessage(): ErrorMessage | null {
    return FormHelper.composeErrorMessages(this.discountPercentage, [
      {
        method: 'getEmptyErrorMessage',
        args: ['Le pourcentage de remise est requis']
      },
      {
        method: 'getMinErrorMessage',
        args: ['Le pourcentage de remise doit être supérieur à 0']
      },
      {
        method: 'getMaxErrorMessage',
        args: ['Le pourcentage de remise doit être inférieur ou égal à 100']
      }
    ]);
  }

  constructor() {
    super(
      {
        id: new FormControl<number | undefined>(undefined),
        type: new FormControl<AnnexLineFormTypeEnum | null>(null, Validators.required),
        label: new FormControl<string | null>(null, Validators.required),
        chargeChoice: new FormControl<string | null>(null, Validators.required),
        discountPercentage: new FormControl<number | null>(null, [Validators.min(1), Validators.max(100)])
      },
      [discountPercentageRequiredValidator()]
    );
  }

  fromContractAnnexLine(line: ContractAnnexLine): void {
    this.patchValue({
      id: line.id,
      type: line.type,
      label: line.label,
      chargeChoice: line.chargeChoice,
      discountPercentage: line.discountPercentage
    });
  }

  toContractAnnexLine(): ContractAnnexLine {
    return new ContractAnnexLine(
      this.id.value,
      this.type.value,
      this.label.value,
      this.chargeChoice.value,
      this.discountPercentage.value
    );
  }

  resetDiscountPercentage(): void {
    this.discountPercentage.reset();
  }
}
